import { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Search, Loader2, ChevronDown, X, FolderOpen, Users } from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { useResourceCategories, useResourceDetail, useResources } from "../hooks/useRepository";
import type { ResourceType } from "../types";
import { RESOURCE_TYPE_LABELS } from "../types";
import { CategorySidebar } from "../components/CategorySidebar";
import { ResourceCard } from "../components/ResourceCard";
import { ResourceDetailModal } from "../components/ResourceDetailModal";
import { ResourceFormModal } from "../components/ResourceFormModal";
import { AccessGroupsModal } from "../components/AccessGroupsModal";

const TYPE_FILTERS: { value: ResourceType | ""; label: string }[] = [
  { value: "", label: "Wszystkie" },
  { value: "document", label: RESOURCE_TYPE_LABELS.document },
  { value: "link", label: RESOURCE_TYPE_LABELS.link },
  { value: "contractor_document", label: RESOURCE_TYPE_LABELS.contractor_document },
];

export function RepositoryPage() {
  const user = useAuthStore((s) => s.user);
  const isDirector = user?.role === "director";
  const isCoordinator = user?.role === "coordinator";
  const isContractor = user?.role === "contractor";
  // Coordinators and directors may create and manage access groups.
  const canManageGroups = isDirector || isCoordinator;

  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<ResourceType | "">("");
  const [search, setSearch] = useState("");
  const [showAddResource, setShowAddResource] = useState(false);
  const [showGroups, setShowGroups] = useState(false);
  const [catDrawerOpen, setCatDrawerOpen] = useState(false);
  // Deep-link: /repository?resource=<id> otwiera szczegóły zasobu (np. „ładna
  // referencja” do szablonów PZT/PAB/PT z zakładki Projekt techniczny).
  const [searchParams, setSearchParams] = useSearchParams();
  const deepResourceId = searchParams.get("resource");
  const { data: deepResource } = useResourceDetail(deepResourceId);
  const closeDeepResource = () => {
    searchParams.delete("resource");
    setSearchParams(searchParams, { replace: true });
  };

  const { data: categories = [], isLoading: catsLoading } = useResourceCategories();
  // A resource can only be created inside a category the user can manage.
  const canAddResource = categories.some((c) => c.can_manage);
  const resourceFilters = useMemo(
    () => ({ category: selectedCategory ?? undefined, type: typeFilter || undefined }),
    [selectedCategory, typeFilter],
  );
  const { data: resources = [], isLoading: resLoading } = useResources(resourceFilters);

  const filtered = search.trim()
    ? resources.filter((r) =>
        r.title.toLowerCase().includes(search.toLowerCase()) ||
        r.description.toLowerCase().includes(search.toLowerCase())
      )
    : resources;

  const selectedCategoryName = selectedCategory
    ? categories.find((c) => c.id === selectedCategory)?.name
    : null;

  return (
    <div className="flex h-full min-h-0 relative">
      {/* Desktop sidebar */}
      {!isContractor && (
        <aside className="hidden md:flex w-64 flex-shrink-0 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 px-3 py-4 overflow-y-auto flex-col">
          {catsLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 size={18} className="animate-spin text-gray-400" />
            </div>
          ) : (
            <CategorySidebar
              categories={categories}
              selectedId={selectedCategory}
              onSelect={setSelectedCategory}
              canCreate={canManageGroups}
            />
          )}
        </aside>
      )}

      {/* Mobile category drawer overlay */}
      {!isContractor && catDrawerOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40 md:hidden"
            onClick={() => setCatDrawerOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 w-64 bg-white dark:bg-gray-900 shadow-xl px-3 py-4 overflow-y-auto flex flex-col md:hidden">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest px-3">
                Kategorie
              </span>
              <button
                onClick={() => setCatDrawerOpen(false)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                <X size={16} />
              </button>
            </div>
            {catsLoading ? (
              <div className="flex justify-center py-4">
                <Loader2 size={18} className="animate-spin text-gray-400" />
              </div>
            ) : (
              <CategorySidebar
                categories={categories}
                selectedId={selectedCategory}
                onSelect={(id) => { setSelectedCategory(id); setCatDrawerOpen(false); }}
                canCreate={canManageGroups}
              />
            )}
          </div>
        </>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 px-4 py-4 md:px-6 md:py-6 overflow-y-auto">
        {/* Toolbar */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <div className="mr-auto">
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
              Repozytorium
            </h1>
            <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-0.5" />
          </div>

          {/* Search */}
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Szukaj…"
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-28 sm:w-40"
            />
          </div>

          {/* Type filter — desktop only in toolbar, mobile handled below */}
          {!isContractor && (
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as ResourceType | "")}
              className="hidden sm:block text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              {TYPE_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          )}

          {canManageGroups && (
            <button
              onClick={() => setShowGroups(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded-lg hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-colors"
            >
              <Users size={15} />
              <span className="hidden sm:inline">Grupy dostępu</span>
            </button>
          )}

          {canAddResource && (
            <button
              onClick={() => setShowAddResource(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:bg-[var(--color-primary-hover)]"
            >
              <Plus size={15} />
              <span>Dodaj zasób</span>
            </button>
          )}
        </div>

        {/* Mobile filter row */}
        {!isContractor && (
          <div className="flex items-center gap-2 mb-4 md:hidden">
            {/* Category button */}
            <button
              onClick={() => setCatDrawerOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:border-[var(--color-primary)] transition-colors min-w-0"
            >
              <FolderOpen size={14} className="flex-shrink-0 text-gray-400" />
              <span className="truncate max-w-[100px]">
                {selectedCategoryName ?? "Wszystkie"}
              </span>
              <ChevronDown size={13} className="flex-shrink-0 text-gray-400" />
            </button>

            {/* Clear category */}
            {selectedCategory && (
              <button
                onClick={() => setSelectedCategory(null)}
                className="p-1.5 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 bg-white dark:bg-gray-800"
              >
                <X size={13} />
              </button>
            )}

            {/* Type filter */}
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as ResourceType | "")}
              className="flex-1 text-sm border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 min-w-0"
            >
              {TYPE_FILTERS.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
        )}

        {/* Resources grid */}
        {resLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 size={28} className="animate-spin text-gray-400" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <p className="text-sm">
              {search ? "Brak wyników dla podanej frazy." : "Brak zasobów."}
            </p>
            {canAddResource && !search && (
              <button
                onClick={() => setShowAddResource(true)}
                className="mt-2 text-sm text-[var(--color-primary)] dark:text-blue-400 hover:underline"
              >
                Dodaj pierwszy zasób
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
            {filtered.map((r) => (
              <ResourceCard key={r.id} resource={r} categories={categories} />
            ))}
          </div>
        )}
      </div>

      {showAddResource && (
        <ResourceFormModal
          categories={categories}
          defaultCategoryId={selectedCategory ?? undefined}
          onClose={() => setShowAddResource(false)}
        />
      )}

      {showGroups && <AccessGroupsModal onClose={() => setShowGroups(false)} />}

      {deepResource && (
        <ResourceDetailModal
          resource={deepResource}
          categories={categories}
          onClose={closeDeepResource}
        />
      )}
    </div>
  );
}
